/**
 * Corporate gift set configurator state: a set of items from the product
 * catalogue, one logo (optionally with a name underneath), packaging and
 * the number of sets.
 */
import type { ArtworkPlacement, GiftPackaging, GiftSetConfiguration, InkSetup, MaterialId } from '@shared/rfq';
import type { ProcessedArtwork } from '../artwork/processArtwork';
import type { UploadStatus } from '../artwork/ArtworkUploader';
import { circumference, defaultContainer, getContainer, type ContainerModel, type SingleProductKind } from '../product/model';

export const GIFT_STEPS = ['items', 'logo', 'personal', 'preview', 'pack', 'rfq'] as const;
export type GiftStepId = (typeof GIFT_STEPS)[number];
export const GIFT_PRODUCTS: SingleProductKind[] = ['candle', 'drinkware', 'bottle', 'diffuser', 'cosmetic'];
export const GIFT_QTY_PRESETS = [25, 50, 100, 250, 500] as const;
export const MAX_ITEMS = GIFT_PRODUCTS.length;

export interface GiftItemState {
  product: SingleProductKind;
  containerId: string;
  material: MaterialId;
  qty: number;
}

export interface GiftState {
  step: number;
  maxStep: number;
  items: GiftItemState[];
  logo: ProcessedArtwork | null;
  upload: { status: UploadStatus; id: string | null };
  designHelp: boolean;
  personal: 'none' | 'names';
  namesText: string;
  showName: boolean;
  logoScale: number;
  /** Vertical offset as a share of band height, −0.3…0.3. */
  offset: number;
  spin: boolean;
  ink: InkSetup;
  packaging: GiftPackaging;
  quantity: number;
  targetDate: string;
}

const item = (product: SingleProductKind, containerId?: string, material?: MaterialId, qty = 1): GiftItemState => {
  const c = containerId ? getContainer(containerId) : defaultContainer(product);
  return { product, containerId: c.id, material: material ?? c.defaultMaterial, qty };
};

export const PRESETS: Record<string, GiftItemState[]> = {
  welcome: [item('candle', 'classic-80', 'frosted'), item('drinkware', 'drink-thermos-500', 'powder_black'), item('bottle', 'bottle-milk-300', 'clear')],
  spa: [item('candle', 'apothecary-85', 'amber'), item('diffuser', 'diffuser-round-100', 'clear'), item('cosmetic', 'cosmetic-jar-200', 'white')],
  coffee: [item('drinkware', 'drink-tumbler-450', 'powder_black', 2), item('bottle', 'jar-deli-250', 'clear')],
};

export const initialGiftState: GiftState = {
  step: 0,
  maxStep: 0,
  items: PRESETS.welcome,
  logo: null,
  upload: { status: 'idle', id: null },
  designHelp: false,
  personal: 'none',
  namesText: '',
  showName: true,
  logoScale: 0.55,
  offset: 0,
  spin: false,
  ink: 'cmyk_white',
  packaging: 'kraft',
  quantity: 50,
  targetDate: '',
};

export type GiftAction =
  | { type: 'goto'; step: number }
  | { type: 'toggleItem'; product: SingleProductKind }
  | { type: 'itemContainer'; product: SingleProductKind; containerId: string }
  | { type: 'itemMaterial'; product: SingleProductKind; material: MaterialId }
  | { type: 'itemQty'; product: SingleProductKind; qty: number }
  | { type: 'preset'; id: string }
  | { type: 'logo'; logo: ProcessedArtwork | null }
  | { type: 'upload'; status: UploadStatus; id?: string | null }
  | { type: 'designHelp'; value: boolean }
  | { type: 'personal'; mode: 'none' | 'names' }
  | { type: 'names'; text: string }
  | { type: 'showName'; value: boolean }
  | { type: 'scale'; value: number }
  | { type: 'offset'; value: number }
  | { type: 'spin'; value: boolean }
  | { type: 'ink'; ink: InkSetup }
  | { type: 'packaging'; value: GiftPackaging }
  | { type: 'quantity'; quantity: number }
  | { type: 'targetDate'; date: string }
  | { type: 'reset' };

export function parseNames(text: string): string[] {
  return text
    .split(/\r?\n/)
    .map((x) => x.trim().slice(0, 60))
    .filter(Boolean)
    .slice(0, 5000);
}

export function giftReducer(s: GiftState, a: GiftAction): GiftState {
  const mapItem = (p: SingleProductKind, f: (i: GiftItemState) => GiftItemState) => s.items.map((i) => (i.product === p ? f(i) : i));
  switch (a.type) {
    case 'goto': {
      const step = Math.max(0, Math.min(GIFT_STEPS.length - 1, a.step));
      return { ...s, step, maxStep: Math.max(s.maxStep, step) };
    }
    case 'toggleItem':
      return s.items.some((i) => i.product === a.product)
        ? { ...s, items: s.items.filter((i) => i.product !== a.product) }
        : { ...s, items: [...s.items, item(a.product)].slice(0, MAX_ITEMS) };
    case 'itemContainer':
      return {
        ...s,
        items: mapItem(a.product, (i) => {
          const c = getContainer(a.containerId);
          return { ...i, containerId: c.id, material: c.materials.includes(i.material) ? i.material : c.defaultMaterial };
        }),
      };
    case 'itemMaterial':
      return { ...s, items: mapItem(a.product, (i) => ({ ...i, material: a.material })) };
    case 'itemQty':
      return { ...s, items: mapItem(a.product, (i) => ({ ...i, qty: Math.max(1, Math.min(6, a.qty)) })) };
    case 'preset':
      return PRESETS[a.id] ? { ...s, items: PRESETS[a.id].map((i) => ({ ...i })) } : s;
    case 'logo':
      return { ...s, logo: a.logo, designHelp: a.logo ? false : s.designHelp, upload: { status: 'idle', id: null } };
    case 'upload':
      return { ...s, upload: { status: a.status, id: a.id ?? s.upload.id } };
    case 'designHelp':
      return { ...s, designHelp: a.value };
    case 'personal':
      return { ...s, personal: a.mode };
    case 'names': {
      const n = parseNames(a.text).length;
      return { ...s, namesText: a.text, quantity: n > 0 ? n : s.quantity };
    }
    case 'showName':
      return { ...s, showName: a.value };
    case 'scale':
      return { ...s, logoScale: Math.max(0.2, Math.min(0.95, a.value)) };
    case 'offset':
      return { ...s, offset: Math.max(-0.3, Math.min(0.3, a.value)) };
    case 'spin':
      return { ...s, spin: a.value };
    case 'ink':
      return { ...s, ink: a.ink };
    case 'packaging':
      return { ...s, packaging: a.value };
    case 'quantity':
      return { ...s, quantity: a.quantity };
    case 'targetDate':
      return { ...s, targetDate: a.date };
    case 'reset':
      return initialGiftState;
  }
}

export function giftBlocker(s: GiftState, step: GiftStepId): 'items' | 'upload' | null {
  if (step === 'items' && s.items.length === 0) return 'items';
  if (step === 'logo' && !s.logo && !s.designHelp) return 'upload';
  return null;
}

/** Logo placement on one item: centred on the front, scaled to the band. */
export function giftPlacement(c: ContainerModel, aspect: number, scale: number, offset: number): ArtworkPlacement {
  const H = c.print.heightMm;
  let h = H * scale;
  let w = h * aspect;
  const maxW = c.diameterMm * 0.9;
  if (w > maxW) {
    w = maxW;
    h = w / aspect;
  }
  const r = c.diameterMm / 2;
  return {
    uMm: Math.round(Math.PI * r * 10) / 10,
    vMm: Math.round((H / 2 + offset * H) * 10) / 10,
    widthMm: Math.round(w * 10) / 10,
    heightMm: Math.round(h * 10) / 10,
    rotationDeg: 0,
  };
}

export function toGiftConfiguration(s: GiftState, logoWarnings: string[], ids: { artworkId: string | null; mockupId: string | null }): GiftSetConfiguration {
  const a = s.logo;
  return {
    product: 'gift',
    items: s.items.map((i) => {
      const c = getContainer(i.containerId);
      return { product: i.product, containerId: c.id, containerLabel: c.name, material: i.material, qtyPerSet: i.qty };
    }),
    ink: s.ink,
    logo: a
      ? {
          artworkId: ids.artworkId,
          fileName: a.fileName,
          mime: a.mime,
          sourceFormat: a.format,
          sizeBytes: a.sizeBytes,
          sha256: a.sha256,
          kind: a.kind,
          pixelWidth: a.pixelWidth,
          pixelHeight: a.pixelHeight,
          effectiveDpi: null,
          hasTransparency: a.hasTransparency,
          ...(a.pdf ? { pdfPages: a.pdf.pages } : {}),
          warnings: logoWarnings,
        }
      : null,
    designHelpRequested: s.designHelp,
    logoScale: s.logoScale,
    personalization: { mode: s.personal, names: s.personal === 'names' ? parseNames(s.namesText) : [] },
    packaging: s.packaging,
    mockupId: ids.mockupId,
  };
}

export function totalCircumference(s: GiftState): number {
  return s.items.reduce((sum, i) => sum + circumference(getContainer(i.containerId)), 0);
}
